import axios from "axios";
import { put, select, SelectEffect, takeLatest } from "redux-saga/effects";
import {
    demandFiltersReceived,
    comparingScenarioChartDataReceived,
    comparingScenarioMapDataReceived,
    dwellTimeDataReceived,
    demandComparisonChartDataReceived,
    comparisonDwellTimeDataReceived,
    demandComparisonSessionDataReceived,
    demandSessionDataReceived
} from "./../actions/Dashboards/index";
import {
    GET_AGGREGATED_DEMAND_MAP_DATA,
    GET_AGGREGATED_DWELL_TIME_DATA,
    GET_COMPARING_SCENARIO_CHART_DATA,
    GET_COMPARING_SCENARIO_MAP_DATA,
    GET_DEMAND_CHART_AGGREGATE_DATA,
    GET_DEMAND_CHART_DATA,
    GET_DEMAND_FILTERS,
    GET_DWELL_TIME_DATA,
    GET_MAP_DATA,
    GET_SUPPLY_CHART_DATA,
    GET_SUPPLY_FILTERS,
    ComparingScenarioChartDataReceivedAction,
    ComparingScenarioDataReceivedAction,
    DemandChartDataReceivedAction,
    DemandFiltersReceivedAction,
    DwellTimeDataReceivedAction,
    GetAggregatedDemandMapDataAction,
    GetAggregatedDwellTimeDataAction,
    GetDemandChartAggregateDataAction,
    GetDemandChartDataAction,
    GetDwellTimeDataAction,
    GetSupplyChartDataAction,
    GetSupplyMapDataAction,
    SupplyChartDataReceivedAction,
    SupplyFiltersReceivedAction,
    SupplyMapDataReceivedAction,
    GET_HISTORICAL_OCCUPANCY,
    GetHistoricalOccupancy,
    HistoricalOccupancyReceived,
    DemandComparisonChartDataReceivedAction,
    ComparisonDwellTimeDataReceivedAction,
    DwellTimeDataType,
    STORE_EXPORT_SUPPLY_CHART_DATA,
    StoreExportSupplyChartDataAction,
    GET_DEMAND_SESSION_DATA,
    GetDemandSessionDataAction,
    DemandSessionDataReceivedAction,
    DemandComparisonSessionDataReceivedAction,
    GET_DEMAND_SESSION_AGGREGATE_DATA,
    GET_DEMAND_COMPARISON_CHART_DATA,
    GET_DEMAND_COMPARISON_CHART_AGGREGATE_DATA,
    GET_DEMAND_COMPARISON_SESSION_AGGREGATE_DATA,
    GET_DEMAND_COMPARISON_SESSION_DATA
} from "../actions/Dashboards/types";
import {
    demandChartDataReceived,
    supplyChartDataReceived,
    supplyFiltersReceived,
    mapDataReceived,
    historicalOccupancyReceived
} from "../actions";
import { getDashboardSnapShots, getRequest, showError } from "../components/commonUtilities";
import { GeneratorYieldType } from "../interfaces/ActionReducer/GetMethod/GetResponse";
import {
    exportFileExtentionForAnalyzer,
    exportFileType
} from "components/curb/ManagerAnalyzer/Manager/ListviewTable/helpers";
import { RootState } from "reducers";
import { ExportFileType } from "actions/regulation/Listview/types";
import { parseDateWithDots } from "Utilities/dateTimeUtilities";
import { AxiosControllerInterface } from "components/curb/ManagerAnalyzer/Analyzer/DashboardNew/GenericCharts/ChartComponents/helpers";

const fileTypeHere = (state: RootState) => state.Dashboards.fileType;
const activeDashboard = (state: RootState) => state.Dashboards.activeDashboard;

function* getSupplyFilters(): Generator<
    | GeneratorYieldType<SupplyFiltersReceivedAction["payload"]>
    | Promise<SupplyFiltersReceivedAction>,
    void,
    SupplyFiltersReceivedAction["payload"]["data"]
> {
    try {
        const json = yield axios.get("/api/supply-filters/").then((response) => response.data);

        yield put(supplyFiltersReceived({ data: json }));
    } catch (error) {
        yield put(supplyFiltersReceived({ data: {} }));
        showError(error);
    }
}

export function* getSupplyFiltersSaga() {
    yield takeLatest(GET_SUPPLY_FILTERS, getSupplyFilters);
}

function* getMapData(
    action: GetSupplyMapDataAction
): Generator<
    | GeneratorYieldType<SupplyMapDataReceivedAction["payload"]>
    | Promise<SupplyMapDataReceivedAction>,
    void,
    SupplyMapDataReceivedAction["payload"]["supplyMapDataMaster"]
> {
    try {
        const json = yield axios
            .post(`/api/${action.payload.dashboard}-map-data/`, action.payload.params)
            .then((response) => response.data);

        yield put(mapDataReceived({ supplyMapDataMaster: json }));
    } catch (error) {
        showError(error);
    }
}

export function* getSupplyMapDataSaga() {
    yield takeLatest(GET_MAP_DATA, getMapData);
}

function* getAggregatedDemandMapData(
    action: GetAggregatedDemandMapDataAction
): Generator<
    | GeneratorYieldType<SupplyMapDataReceivedAction["payload"]>
    | Promise<SupplyMapDataReceivedAction>,
    void,
    SupplyMapDataReceivedAction["payload"]["supplyMapDataMaster"]
> {
    try {
        const json = yield axios
            .post(`/api/${action.payload.dashboard}-map-aggregate-data/`, action.payload.params)
            .then((response) => response.data);

        yield put(mapDataReceived({ supplyMapDataMaster: json }));
    } catch (error) {
        showError(error);
    }
}

export function* getAggregatedMapDataSaga() {
    yield takeLatest(GET_AGGREGATED_DEMAND_MAP_DATA, getAggregatedDemandMapData);
}

function* getComparingScenarioMapData(
    action: GetSupplyMapDataAction
): Generator<
    | GeneratorYieldType<ComparingScenarioDataReceivedAction["payload"]>
    | Promise<ComparingScenarioDataReceivedAction>,
    void,
    ComparingScenarioDataReceivedAction["payload"]["supplyMapDataMaster"]
> {
    try {
        const json = yield axios
            .post(`/api/${action.payload.dashboard}-map-data/`, action.payload.params)
            .then((response) => response.data);

        yield put(comparingScenarioMapDataReceived({ supplyMapDataMaster: json }));
    } catch (error) {
        showError(error);
    }
}

function* getHistoricalOccupancy(
    action: GetHistoricalOccupancy
): Generator<
    | GeneratorYieldType<HistoricalOccupancyReceived["payload"]>
    | Promise<HistoricalOccupancyReceived>,
    void,
    HistoricalOccupancyReceived["payload"]["data"]
> {
    try {
        const json = yield getRequest("/api/historic-aggregate-occupancy/", action.payload.params);
        yield put(historicalOccupancyReceived({ data: json }));
    } catch (error) {
        showError(error);
    }
}

export function* getHistoricalOccupancySaga() {
    yield takeLatest(GET_HISTORICAL_OCCUPANCY, getHistoricalOccupancy);
}

export function* getComparingScenarioMapDataSaga() {
    yield takeLatest(GET_COMPARING_SCENARIO_MAP_DATA, getComparingScenarioMapData);
}

function* getSupplyChartData(
    action: GetSupplyChartDataAction
): Generator<
    | GeneratorYieldType<SupplyChartDataReceivedAction["payload"]>
    | Promise<SupplyChartDataReceivedAction>,
    void,
    SupplyChartDataReceivedAction["payload"]["data"]
> {
    try {
        const json = yield axios
            .post("/api/supply-charts-data/", action.payload.params)
            .then((response) => response.data);

        yield put(supplyChartDataReceived({ data: json }));
    } catch (error) {
        showError(error);
        yield put(supplyChartDataReceived({ data: {} }));
    }
}

export function* getSupplyChartDataSaga() {
    yield takeLatest(GET_SUPPLY_CHART_DATA, getSupplyChartData);
}

function* getComparingScenarioChartData(
    action: GetSupplyChartDataAction
): Generator<
    | GeneratorYieldType<ComparingScenarioChartDataReceivedAction["payload"]>
    | Promise<ComparingScenarioChartDataReceivedAction>,
    void,
    ComparingScenarioChartDataReceivedAction["payload"]["data"]
> {
    try {
        const json = yield axios
            .post("/api/supply-charts-data/", action.payload.params)
            .then((response) => response.data);

        yield put(comparingScenarioChartDataReceived({ data: json }));
    } catch (error) {
        showError(error);
        yield put(comparingScenarioChartDataReceived({ data: {} }));
    }
}

export function* getComparingScenarioChartDataSaga() {
    yield takeLatest(GET_COMPARING_SCENARIO_CHART_DATA, getComparingScenarioChartData);
}

function* getDemandChartData(
    action: GetDemandChartDataAction
): Generator<
    | GeneratorYieldType<DemandChartDataReceivedAction["payload"]>
    | Promise<DemandChartDataReceivedAction>,
    void,
    DemandChartDataReceivedAction["payload"]["demandChartData"]
> {
    try {
        const controller = new AbortController();
        const json = yield axios
            .post("/api/demand-chart-data/", action.payload.params, {
                signal: controller.signal,
                controller: controller
            } as AxiosControllerInterface)
            .then((response) => response.data);
        yield put(
            demandChartDataReceived({
                demandChartData: json,
                type: "hourly-(all-days)"
            })
        );
    } catch (error) {
        showError(error);
        yield put(
            demandChartDataReceived({
                demandChartData: {},
                type: ""
            })
        );
    }
}

export function* getDemandChartDataSaga() {
    yield takeLatest(GET_DEMAND_CHART_DATA, getDemandChartData);
}

function* getDemandComparisonChartData(
    action: GetDemandChartDataAction
): Generator<
    | GeneratorYieldType<DemandComparisonChartDataReceivedAction["payload"]>
    | Promise<DemandComparisonChartDataReceivedAction>,
    void,
    DemandComparisonChartDataReceivedAction["payload"]["demandComparisonChartData"]
> {
    try {
        const json = yield axios
            .post("/api/demand-chart-data/", action.payload.params)
            .then((response) => response.data);

        yield put(demandComparisonChartDataReceived({ demandComparisonChartData: json }));
    } catch (error) {
        showError(error);
        yield put(demandComparisonChartDataReceived({ demandComparisonChartData: {} }));
    }
}

export function* getDemandComparisonChartDataSaga() {
    yield takeLatest(GET_DEMAND_COMPARISON_CHART_DATA, getDemandComparisonChartData);
}

function* getDemandSessionData(
    action: GetDemandSessionDataAction
): Generator<
    | GeneratorYieldType<DemandSessionDataReceivedAction["payload"]>
    | Promise<DemandSessionDataReceivedAction>,
    void,
    DemandSessionDataReceivedAction["payload"]["demandSessionData"]
> {
    try {
        const json = yield axios
            .post("/api/demand-session-data/", action.payload.params)
            .then((response) => response.data);
        yield put(
            demandSessionDataReceived({
                demandSessionData: json
            })
        );
    } catch (error) {
        showError(error);
        yield put(
            demandSessionDataReceived({
                demandSessionData: {}
            })
        );
    }
}

export function* getDemandSessionDataSaga() {
    yield takeLatest(GET_DEMAND_SESSION_DATA, getDemandSessionData);
}

function* getDemandComparisonSessionData(
    action: GetDemandSessionDataAction
): Generator<
    | GeneratorYieldType<DemandComparisonSessionDataReceivedAction["payload"]>
    | Promise<DemandComparisonSessionDataReceivedAction>,
    void,
    DemandComparisonSessionDataReceivedAction["payload"]["demandComparisonSessionData"]
> {
    try {
        const json = yield axios
            .post("/api/demand-session-data/", action.payload.params)
            .then((response) => response.data);

        yield put(demandComparisonSessionDataReceived({ demandComparisonSessionData: json }));
    } catch (error) {
        showError(error);
        yield put(demandComparisonSessionDataReceived({ demandComparisonSessionData: {} }));
    }
}

export function* getDemandComparisonSessionDataSaga() {
    yield takeLatest(GET_DEMAND_COMPARISON_SESSION_DATA, getDemandComparisonSessionData);
}

function* getDemandSessionAggregateData(
    action: GetDemandSessionDataAction
): Generator<
    | GeneratorYieldType<DemandSessionDataReceivedAction["payload"]>
    | Promise<DemandSessionDataReceivedAction>,
    void,
    DemandSessionDataReceivedAction["payload"]["demandSessionData"]
> {
    try {
        const json = yield axios
            .post("/api/demand-session-aggregate-data/", action.payload.params)
            .then((response) => response.data);

        yield put(
            demandSessionDataReceived({
                demandSessionData: json
            })
        );
    } catch (error) {
        showError(error);
        yield put(
            demandSessionDataReceived({
                demandSessionData: {}
            })
        );
    }
}

export function* getDemandSessionAggregateDataSaga() {
    yield takeLatest(GET_DEMAND_SESSION_AGGREGATE_DATA, getDemandSessionAggregateData);
}

function* getDemandComparisonSessionAggregateData(
    action: GetDemandSessionDataAction
): Generator<
    | GeneratorYieldType<DemandComparisonSessionDataReceivedAction["payload"]>
    | Promise<DemandComparisonSessionDataReceivedAction>,
    void,
    DemandComparisonSessionDataReceivedAction["payload"]["demandComparisonSessionData"]
> {
    try {
        const json = yield axios
            .post("/api/demand-session-aggregate-data/", action.payload.params)
            .then((response) => response.data);

        yield put(demandComparisonSessionDataReceived({ demandComparisonSessionData: json }));
    } catch (error) {
        showError(error);
        yield put(demandComparisonSessionDataReceived({ demandComparisonSessionData: {} }));
    }
}

export function* getDemandComparisonSessionAggregateDataSaga() {
    yield takeLatest(
        GET_DEMAND_COMPARISON_SESSION_AGGREGATE_DATA,
        getDemandComparisonSessionAggregateData
    );
}

function* getDemandFilters(
    action: GetDemandChartDataAction
): Generator<
    | GeneratorYieldType<DemandFiltersReceivedAction["payload"]>
    | Promise<DemandFiltersReceivedAction["payload"]>,
    void,
    DemandFiltersReceivedAction["payload"]["filters"]
> {
    try {
        const json = yield axios.get("/api/demand-filters/").then((response) => response.data);

        yield put(demandFiltersReceived({ filters: json }));
    } catch (error: any) {
        showError(error);
        yield put(
            demandFiltersReceived({
                filters: {
                    demand_data: {},
                    source_data: {},
                    error: (error.response?.data?.detail as string) || ""
                }
            })
        );
    }
}

export function* getDemandFiltersSaga() {
    yield takeLatest(GET_DEMAND_FILTERS, getDemandFilters);
}

function* getDwellTimeData(
    action: GetDwellTimeDataAction
): Generator<
    | GeneratorYieldType<
          DwellTimeDataReceivedAction["payload"] | ComparisonDwellTimeDataReceivedAction["payload"]
      >
    | Promise<DwellTimeDataReceivedAction>,
    void,
    DwellTimeDataReceivedAction["payload"]["dwellTimeData"]
> {
    try {
        const json = yield axios
            .get("/api/turnover-dwelltime/", { params: action.payload.params })
            .then((response) => response.data);

        if (action.payload.isCompare) {
            yield put(comparisonDwellTimeDataReceived({ comparisonDwellTimeData: json ?? {} }));
        } else {
            yield put(dwellTimeDataReceived({ dwellTimeData: json ?? {} }));
        }
    } catch (error) {
        showError(error);
        yield put(dwellTimeDataReceived({ dwellTimeData: { aggregate: {}, hourly: {} } }));
    }
}

export function* getDwellTimeDataSaga() {
    yield takeLatest(GET_DWELL_TIME_DATA, getDwellTimeData);
}

function* getDemandChartAggregateData(
    action: GetDemandChartAggregateDataAction
): Generator<
    | DemandChartDataReceivedAction["payload"]["demandChartData"]
    | GeneratorYieldType<DemandChartDataReceivedAction["payload"]>
    | Promise<DemandChartDataReceivedAction>,
    void,
    DemandChartDataReceivedAction["payload"]["demandChartData"]
> {
    try {
        const controller = new AbortController();
        const json = yield axios
            .post("/api/demand-chart-aggregate-data/", action.payload.params, {
                signal: controller.signal,
                controller: controller
            } as AxiosControllerInterface)
            .then((response) => response.data);

        yield put(
            demandChartDataReceived({
                demandChartData: json,
                type: "hourly-average"
            })
        );
    } catch (error) {
        showError(error);
        yield put(
            demandChartDataReceived({
                demandChartData: {},
                type: ""
            })
        );
    }
}

export function* getDemandChartAggregateDataSaga() {
    yield takeLatest(GET_DEMAND_CHART_AGGREGATE_DATA, getDemandChartAggregateData);
}

function* getDemandComparisonChartAggregateData(
    action: GetDemandChartAggregateDataAction
): Generator<
    | GeneratorYieldType<DemandComparisonChartDataReceivedAction["payload"]>
    | Promise<DemandComparisonChartDataReceivedAction>,
    void,
    DemandComparisonChartDataReceivedAction["payload"]["demandComparisonChartData"]
> {
    try {
        const json = yield axios
            .post("/api/demand-chart-aggregate-data/", action.payload.params)
            .then((response) => response.data);
        yield put(demandComparisonChartDataReceived({ demandComparisonChartData: json }));
    } catch (error) {
        showError(error);
        yield put(demandComparisonChartDataReceived({ demandComparisonChartData: {} }));
    }
}

export function* getDemandComparisonChartAggregateDataSaga() {
    yield takeLatest(
        GET_DEMAND_COMPARISON_CHART_AGGREGATE_DATA,
        getDemandComparisonChartAggregateData
    );
}

function* getAggregatedDwellTimeData(
    action: GetAggregatedDwellTimeDataAction
): Generator<
    | GeneratorYieldType<DwellTimeDataReceivedAction["payload"]>
    | Promise<DwellTimeDataReceivedAction>,
    void,
    DwellTimeDataReceivedAction["payload"]["dwellTimeData"]
> {
    try {
        const json = yield axios
            .post("/api/turnover-aggregate-dwelltime/", action.payload.params)
            .then((response) => response.data);

        yield put(dwellTimeDataReceived({ dwellTimeData: json ?? {} }));
    } catch (error) {
        showError(error);
        yield put(dwellTimeDataReceived({ dwellTimeData: { aggregate: {}, hourly: {} } }));
    }
}

export function* getAggregatedDwellTimeDataSaga() {
    yield takeLatest(GET_AGGREGATED_DWELL_TIME_DATA, getAggregatedDwellTimeData);
}

function* downloadExportedFileHere(
    action: StoreExportSupplyChartDataAction
): Generator<SelectEffect | Promise<any>, void, any> {
    try {
        const blobFileTypes = new Set([exportFileType.pdf, exportFileType.excel]);
        const fileType: string = yield select(fileTypeHere);
        const currentDashboard: string = yield select(activeDashboard);
        let url = `/api/${currentDashboard}${
            currentDashboard.includes("comparison") ? "" : "-insights"
        }-data-export/`;
        const fileName: string = `${currentDashboard.includes("supply") ? "Supply" : "Demand"}_${
            currentDashboard.includes("comparison") ? "Comparison" : "Insights"
        }_Export_${parseDateWithDots(new Date())}`;
        let payload = {
            ...action.payload.data
        };
        if (action.payload.data.export_type === "pdf") {
            const dashboardSnapshots = yield getDashboardSnapShots();
            payload = {
                ...payload,
                dashboard_snapshots: dashboardSnapshots
            };
        }
        yield axios
            .post(url, payload, {
                responseType: blobFileTypes.has(fileType as ExportFileType) ? "blob" : "json",
                withCredentials: true
            })
            .then((res) => {
                const link = document.createElement("a");
                const blob = res.data;
                if (blobFileTypes.has(fileType as ExportFileType)) {
                    link.href = window.URL.createObjectURL(blob);
                } else {
                    link.href = `data:text/json;charset=utf-8,${encodeURIComponent(
                        JSON.stringify(blob)
                    )}`;
                }
                link.download =
                    fileName + exportFileExtentionForAnalyzer[fileType as ExportFileType];
                link.click();
                link.remove();
            })
            .finally(() => {
                action.payload.afterExport();
                localStorage.removeItem("isDownloadingCharts");
            });
    } catch (error) {
        showError(error);
        localStorage.removeItem("isDownloadingCharts");
    }
}

export function* downloadExportedFileSaga() {
    yield takeLatest(STORE_EXPORT_SUPPLY_CHART_DATA, downloadExportedFileHere);
}
